/**
 * 1.4.0 asset groups, stream G2: the group page (/groups/:id, App.tsx). The header (poster, title, type, breadcrumb,
 * description, the metadata form, editable with group.manage), child groups, the members table in order (drag to
 * reorder, inline numbers, Renumber, Add assets, Remove), the delivery column and summary, Assign for delivery… and
 * Assign the unassigned… (portal.assign), and the selection bar's Export, Run workflow and Add to collection (the
 * Library's own LibrarySelectionBar). Hooks: api/hooks/groups.ts (useGroup, useGroupMembers, …),
 * api/hooks/groups-admin.ts. G2's file.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FolderTree, GripVertical, ImageIcon, ListOrdered, Loader2, Plus, Save, Search, Trash2, Upload, X } from 'lucide-react';
import type { AssetGroupDetailDTO, AssetGroupDTO, AssetSummary, GroupMemberDTO, GroupTypeDTO } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMe } from '../../api/hooks';
import {
  useAddGroupMembers, useCreateGroup, useDeleteGroup, useGroup, useGroupMembers, useGroupTypes, useOrderGroupMembers, usePatchGroup,
  usePatchGroupMember, useRemoveGroupMembers, useRemoveGroupPoster, useUploadGroupPoster,
} from '../../api/hooks/groups';
import { useLibrary } from '../../api/hooks/library';
import { MetadataSections } from '../../components/MetadataFields';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { ASSIGNMENT_STATE_LABEL } from '../assignments/DeliveryPanel';
import { LibrarySelectionBar } from '../library/LibrarySelectionBar';
import { AddToGroupDialog, GroupTypeIcon } from './AddToGroup';
import { AssignGroupForDeliveryButton } from './AssignGroupForDelivery';

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const DELIVERY_PILL: Record<string, string> = { open: 'accent', in_progress: 'accent', in_review: 'warning', accepted: 'ready', cancelled: '', expired: 'danger' };

/** "8 of 12 accepted · 3 open · 1 in review". */
export function deliverySummaryText(d: NonNullable<AssetGroupDetailDTO['deliveries']>): string {
  const parts = [`${d.accepted} of ${d.placeholders} accepted`];
  if (d.open) parts.push(`${d.open} open`);
  if (d.uploaded) parts.push(`${d.uploaded} in progress`);
  if (d.inReview) parts.push(`${d.inReview} in review`);
  return parts.join(' · ');
}

/** The new order after dragging `moved` onto `target` (before it, or after it when coming from above). */
export function reorder(ids: string[], moved: string, target: string): string[] {
  if (moved === target) return ids;
  const from = ids.indexOf(moved);
  const to = ids.indexOf(target);
  if (from < 0 || to < 0) return ids;
  const next = ids.filter((x) => x !== moved);
  next.splice(next.indexOf(target) + (from < to ? 1 : 0), 0, moved);
  return next;
}

export function GroupPage() {
  const { id } = useParams();
  const q = useGroup(id);
  const types = useGroupTypes().data?.items ?? [];
  const g = q.data;
  return (
    <div className="page group-page" data-testid="group-page" data-group-id={id}>
      <header className="topbar">
        <Link to="/library" className="btn ghost icon" aria-label="Back to library"><ArrowLeft size={17} /></Link>
        <nav className="group-breadcrumb" aria-label="Groups" data-testid="group-breadcrumb">
          <Link to="/library">Library</Link>
          {g?.path.map((c) => <span key={c.id}><span className="asset-group-sep" aria-hidden> › </span><Link to={`/groups/${c.id}`}>{c.title}</Link></span>)}
          {g && <span><span className="asset-group-sep" aria-hidden> › </span><strong>{g.title}</strong></span>}
        </nav>
      </header>
      {q.isPending ? <div className="skeleton" style={{ height: 240 }} />
        : q.isError || !g ? <div className="empty"><p>{q.error ? errorMessage(q.error) : 'Group not found'}</p><Link to="/library" className="btn">Back to the Library</Link></div>
          : <GroupBody key={g.id} g={g} type={types.find((t) => t.id === g.typeId) ?? null} types={types} />}
    </div>
  );
}

function GroupBody({ g, type, types }: { g: AssetGroupDetailDTO; type: GroupTypeDTO | null; types: GroupTypeDTO[] }) {
  const perms = useMe().data?.permissions ?? [];
  const can = perms.includes('group.manage');
  return (
    <>
      <GroupHeader g={g} type={type} canEdit={can} />
      {(g.deliveries && g.deliveries.placeholders > 0) || perms.includes('portal.assign') ? (
        <section className="group-delivery" data-testid="group-delivery">
          {g.deliveries && g.deliveries.placeholders > 0
            ? <span className="group-delivery-summary" data-testid="group-delivery-summary">{deliverySummaryText(g.deliveries)}</span>
            : <span className="faint">No deliveries yet.</span>}
          <span style={{ flex: 1 }} />
          <AssignGroupForDeliveryButton groupId={g.id} title={g.title} />
          {g.deliveries && g.deliveries.open + g.deliveries.uploaded + g.deliveries.inReview + g.deliveries.accepted > 0 && <AssignGroupForDeliveryButton groupId={g.id} title={g.title} onlyUnassigned />}
        </section>
      ) : null}
      <ChildGroups g={g} types={types} canEdit={can} />
      <Members g={g} type={type} canEdit={can} />
    </>
  );
}

// ---------------------------------------------------------------- header

function GroupHeader({ g, type, canEdit }: { g: AssetGroupDetailDTO; type: GroupTypeDTO | null; canEdit: boolean }) {
  const patch = usePatchGroup();
  const navigate = useNavigate();
  const [title, setTitle] = useState(g.title);
  const [description, setDescription] = useState(g.description);
  const [number, setNumber] = useState(g.number === null ? '' : String(g.number));
  const [metadata, setMetadata] = useState<Record<string, unknown>>(g.metadata ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [poster, setPoster] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { setTitle(g.title); setDescription(g.description); setNumber(g.number === null ? '' : String(g.number)); setMetadata(g.metadata ?? {}); }, [g]);
  const fields = (type?.fields ?? []).filter((f) => !f.hidden);
  const schema = useMemo(() => ({ entityType: 'asset' as const, sections: [{ id: 'group', label: `${g.type.label} details` }], fields: fields.map((f) => ({ ...f, section: 'group' })) }), [fields, g.type.label]);
  const num = number.trim() === '' ? null : Number(number);
  const dirty = title.trim() !== g.title || description !== g.description || num !== g.number || JSON.stringify(metadata) !== JSON.stringify(g.metadata ?? {});

  async function save() {
    try {
      await patch.mutateAsync({ id: g.id, body: { title: title.trim(), description, number: Number.isFinite(num) ? num : null, metadata } });
      setErrors({});
      toast(`${g.type.label} saved`);
    } catch (err) {
      const fieldErrors = (err as { details?: { fields?: Record<string, string> } }).details?.fields;
      if (fieldErrors) setErrors(fieldErrors);
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <section className="group-header" data-testid="group-header">
      <div className="group-poster">
        {g.posterUrl ? <img src={g.posterUrl} alt="" data-testid="group-poster-img" /> : <div className="group-poster-empty"><GroupTypeIcon icon={type?.icon} size={36} /></div>}
        {canEdit && <button className="btn ghost sm" onClick={() => setPoster(true)} data-testid="group-poster-change"><ImageIcon size={13} /> Poster…</button>}
      </div>
      <div className="group-header-main">
        <div className="group-header-type">
          <span className="pill" data-testid="group-type-pill">{g.type.label}</span>
          {g.type.numberLabel && g.number !== null && <span className="faint">{g.type.numberLabel} {g.number}</span>}
          <span className="faint">{plural(g.memberCount, g.type.memberLabel.toLowerCase())}{g.childCount ? ` · ${plural(g.descendantMemberCount, 'asset')} in all` : ''}</span>
        </div>
        {canEdit
          ? <input className="input group-title-input" value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} aria-label="Title" data-testid="group-title" />
          : <h1 data-testid="group-title">{g.title}</h1>}
        {canEdit
          ? <textarea className="input" rows={2} value={description} maxLength={20000} onChange={(e) => setDescription(e.target.value)} placeholder="Description" aria-label="Description" data-testid="group-description" />
          : g.description && <p className="group-description">{g.description}</p>}
        {g.type.numberLabel && canEdit && (
          <div className="field group-number-field"><label htmlFor="group-number">{g.type.numberLabel}</label>
            <input id="group-number" className="input" type="number" min={0} value={number} onChange={(e) => setNumber(e.target.value)} data-testid="group-number" /></div>
        )}
        {fields.length > 0 && (
          <div className="group-metadata" data-testid="group-metadata">
            <MetadataSections schema={schema} values={metadata} categoryId={null} errors={errors} onChange={(fid, v) => setMetadata((m) => ({ ...m, [fid]: v }))} canEdit={canEdit} />
          </div>
        )}
        {canEdit && (
          <div className="group-header-actions">
            <button className="btn primary sm" disabled={!dirty || !title.trim() || patch.isPending} onClick={() => void save()} data-testid="group-save">
              {patch.isPending ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
            </button>
            {dirty && <button className="btn ghost sm" onClick={() => { setTitle(g.title); setDescription(g.description); setNumber(g.number === null ? '' : String(g.number)); setMetadata(g.metadata ?? {}); }}>Discard</button>}
            <span style={{ flex: 1 }} />
            <button className="btn ghost sm danger" onClick={() => setDeleting(true)} data-testid="group-delete"><Trash2 size={13} /> Delete {g.type.label.toLowerCase()}</button>
          </div>
        )}
      </div>
      {poster && <PosterDialog g={g} onClose={() => setPoster(false)} />}
      {deleting && <DeleteGroupDialog g={g} onClose={() => setDeleting(false)} onDeleted={() => navigate(g.path.length ? `/groups/${g.path[g.path.length - 1]!.id}` : '/library')} />}
    </section>
  );
}

function PosterDialog({ g, onClose }: { g: AssetGroupDetailDTO; onClose: () => void }) {
  const patch = usePatchGroup();
  const uploadPoster = useUploadGroupPoster();
  const removePoster = useRemoveGroupPoster();
  const members = useGroupMembers(g.id).data?.items ?? [];
  const withThumb = members.filter((m) => m.asset.thumbnailUrl);
  const file = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  async function pickMember(assetId: string | null) {
    try {
      if (assetId === null) await removePoster.mutateAsync(g.id);
      else await patch.mutateAsync({ id: g.id, body: { posterAssetId: assetId } });
      toast(assetId ? 'Poster set' : 'Poster removed'); onClose();
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  async function upload(f: File) {
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) { toast('Choose a PNG, JPEG or WebP image', 'error'); return; }
    setUploading(true);
    try {
      await uploadPoster.mutateAsync({ id: g.id, file: f });
      toast('Poster uploaded');
      onClose();
    } catch (err) { toast(errorMessage(err), 'error'); } finally { setUploading(false); }
  }
  return (
    <Modal title={`Poster of ${g.title}`} wide onClose={onClose} actions={<>
      {(g.posterUrl || g.posterAssetId) && <button className="btn ghost" onClick={() => void pickMember(null)}>Remove poster</button>}
      <span style={{ flex: 1 }} />
      <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} data-testid="group-poster-file" />
      <button className="btn" disabled={uploading} onClick={() => file.current?.click()}>{uploading ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} Upload an image…</button>
      <button className="btn ghost" onClick={onClose}>Close</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>Pick a member's thumbnail, or upload an image (PNG, JPEG or WebP).</p>
      <div className="group-poster-grid" data-testid="group-poster-grid">
        {withThumb.map((m) => (
          <button key={m.asset.id} type="button" className={`group-poster-pick ${g.posterAssetId === m.asset.id ? 'selected' : ''}`} onClick={() => void pickMember(m.asset.id)} title={m.asset.title} data-testid="group-poster-pick">
            <img src={m.asset.thumbnailUrl!} alt="" /><span>{m.asset.title}</span>
          </button>
        ))}
        {!withThumb.length && <p className="faint">No member has a thumbnail yet.</p>}
      </div>
    </Modal>
  );
}

function DeleteGroupDialog({ g, onClose, onDeleted }: { g: AssetGroupDetailDTO; onClose: () => void; onDeleted: () => void }) {
  const del = useDeleteGroup();
  const [children, setChildren] = useState<'move_up' | 'delete'>('move_up');
  async function go() {
    try { await del.mutateAsync({ id: g.id, children: g.childCount ? children : undefined }); toast(`${g.title} deleted`); onDeleted(); } catch (err) { toast(errorMessage(err), 'error'); }
  }
  return (
    <Modal title={`Delete ${g.title}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn danger" disabled={del.isPending} onClick={() => void go()} data-testid="group-delete-confirm"><Trash2 size={14} /> Delete</button>
    </>}>
      <p style={{ marginTop: 0 }}>Its {plural(g.memberCount, g.type.memberLabel.toLowerCase())} stay in the Library, in no {g.type.label.toLowerCase()}.</p>
      {g.childCount > 0 && (
        <fieldset className="group-delete-children">
          <legend>Its {plural(g.childCount, 'group')} inside</legend>
          <label><input type="radio" checked={children === 'move_up'} onChange={() => setChildren('move_up')} /> Move them up a level</label>
          <label><input type="radio" checked={children === 'delete'} onChange={() => setChildren('delete')} /> Delete them too (their assets stay in the Library)</label>
        </fieldset>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- child groups

function ChildGroups({ g, types, canEdit }: { g: AssetGroupDetailDTO; types: GroupTypeDTO[]; canEdit: boolean }) {
  const childTypes = types.filter((t) => t.parentTypeId === g.typeId && !t.archived);
  const create = useCreateGroup();
  const [adding, setAdding] = useState<GroupTypeDTO | null>(null);
  const [title, setTitle] = useState('');
  const [number, setNumber] = useState('');
  if (!g.children.length && !(canEdit && childTypes.length)) return null;
  async function add() {
    if (!adding) return;
    const n = number.trim() === '' ? null : Number(number);
    try {
      await create.mutateAsync({ typeId: adding.id, parentId: g.id, title: title.trim(), number: Number.isFinite(n) ? n : null });
      toast(`${adding.label} ${title.trim()} added`);
      setAdding(null); setTitle(''); setNumber('');
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  return (
    <section className="group-children" data-testid="group-children">
      <div className="group-section-head">
        <h2>{childTypes.length === 1 ? childTypes[0]!.pluralLabel : 'Inside'}</h2>
        <span style={{ flex: 1 }} />
        {canEdit && childTypes.map((t) => (
          <button key={t.id} className="btn ghost sm" onClick={() => { setAdding(t); setNumber(String((g.children.filter((c) => c.typeId === t.id).reduce((m, c) => Math.max(m, c.number ?? 0), 0)) + 1)); }} data-testid="group-child-new" data-type={t.key}>
            <Plus size={13} /> New {t.label.toLowerCase()}
          </button>
        ))}
      </div>
      {adding && (
        <div className="group-child-new-form">
          <input className="input" autoFocus value={title} maxLength={300} placeholder={`${adding.label} title, e.g. ${adding.label} ${number || 1}`} onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) void add(); if (e.key === 'Escape') setAdding(null); }} data-testid="group-child-title" aria-label={`${adding.label} title`} />
          {adding.numberLabel && <input className="input group-child-number" type="number" min={0} value={number} onChange={(e) => setNumber(e.target.value)} aria-label={adding.numberLabel} />}
          <button className="btn primary sm" disabled={!title.trim() || create.isPending} onClick={() => void add()} data-testid="group-child-create">Add</button>
          <button className="btn ghost icon sm" aria-label="Cancel" onClick={() => setAdding(null)}><X size={13} /></button>
        </div>
      )}
      <div className="group-child-cards">
        {g.children.map((c) => <ChildCard key={c.id} c={c} />)}
      </div>
    </section>
  );
}

function ChildCard({ c }: { c: AssetGroupDTO }) {
  return (
    <Link to={`/groups/${c.id}`} className="group-child-card" data-testid="group-child" data-title={c.title}>
      {c.posterUrl ? <img src={c.posterUrl} alt="" /> : <div className="group-child-poster"><FolderTree size={20} /></div>}
      <span className="group-child-title">{c.title}</span>
      <span className="faint">{c.type.numberLabel && c.number !== null ? `${c.type.numberLabel} ${c.number} · ` : ''}{plural(c.descendantMemberCount, c.type.memberLabel.toLowerCase())}</span>
    </Link>
  );
}

// ---------------------------------------------------------------- members

function Members({ g, type, canEdit }: { g: AssetGroupDetailDTO; type: GroupTypeDTO | null; canEdit: boolean }) {
  const q = useGroupMembers(g.id);
  const loaded = useMemo(() => q.data?.items ?? [], [q.data]);
  const order = useOrderGroupMembers();
  const remove = useRemoveGroupMembers();
  const [ids, setIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => { setIds(loaded.map((m) => m.asset.id)); }, [loaded]);
  const byId = useMemo(() => new Map(loaded.map((m) => [m.asset.id, m])), [loaded]);
  const rows = ids.map((id) => byId.get(id)).filter((m): m is GroupMemberDTO => Boolean(m));
  const numbered = g.type.numbering !== 'none';
  const showDelivery = rows.some((m) => m.delivery) || Boolean(g.deliveries);
  const canOrder = canEdit && !q.hasMore;
  const selectedAssets: AssetSummary[] = rows.filter((m) => selected.has(m.asset.id)).map((m) => m.asset);

  async function saveOrder(next: string[], renumber = false) {
    const before = ids;
    setIds(next);
    try {
      await order.mutateAsync({ id: g.id, assetIds: next, renumber });
      if (renumber) toast(`${plural(next.length, g.type.memberLabel.toLowerCase())} numbered 1–${next.length}`);
    } catch (err) {
      setIds(before);
      toast(errorMessage(err), 'error');
    }
  }
  async function removeSelected() {
    const list = [...selected];
    if (!window.confirm(`Take ${plural(list.length, 'asset')} out of ${g.title}? They stay in the Library.`)) return;
    try { const r = await remove.mutateAsync({ id: g.id, assetIds: list }); setSelected(new Set()); toast(`${plural(r.removed, 'asset')} removed from ${g.title}`); } catch (err) { toast(errorMessage(err), 'error'); }
  }
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = rows.length > 0 && rows.every((m) => selected.has(m.asset.id));

  return (
    <section className="group-members" data-testid="group-members">
      <div className="group-section-head">
        <h2>{type?.memberLabel && type.memberLabel !== 'Asset' ? `${type.memberLabel}s` : 'Members'} <span className="faint">{g.memberCount.toLocaleString()}</span></h2>
        <span style={{ flex: 1 }} />
        {canEdit && numbered && rows.length > 0 && (
          <button className="btn ghost sm" disabled={!canOrder || order.isPending} onClick={() => {
            if (window.confirm(`Number the ${plural(rows.length, g.type.memberLabel.toLowerCase())} 1–${rows.length} in the order shown?`)) void saveOrder(ids, true);
          }} data-testid="group-renumber" title={q.hasMore ? 'Load every member first' : 'Number the members 1…n in the order shown'}><ListOrdered size={13} /> Renumber</button>
        )}
        {canEdit && <button className="btn sm" onClick={() => setAdding(true)} data-testid="group-add-assets"><Plus size={13} /> Add assets…</button>}
      </div>
      {selected.size > 0 && (
        <div className="group-selection">
          <LibrarySelectionBar selected={selectedAssets} onClear={() => setSelected(new Set())} />
          {canEdit && <button className="btn ghost sm danger" onClick={() => void removeSelected()} disabled={remove.isPending} data-testid="group-remove-members"><X size={13} /> Remove {selected.size} from {g.type.label.toLowerCase()}</button>}
        </div>
      )}
      {q.isPending ? <div className="skeleton" style={{ height: 160 }} /> : !rows.length ? (
        <div className="empty group-members-empty"><p className="muted">No {g.type.memberLabel.toLowerCase()}s yet.{canEdit ? ' Add assets here, or select them in the Library and use Add to group.' : ''}</p></div>
      ) : (
        <table className="table group-members-table" data-testid="group-members-table">
          <thead>
            <tr>
              {canEdit && <th className="group-col-grip" aria-label="Drag to reorder" />}
              <th className="group-col-check"><input type="checkbox" aria-label="Select all" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(rows.map((m) => m.asset.id)))} /></th>
              {numbered && <th className="group-col-number">{g.type.memberLabel === 'Asset' ? 'No.' : `${g.type.memberLabel} no.`}</th>}
              <th className="group-col-thumb" aria-label="Thumbnail" />
              <th>Title</th>
              <th>Code</th>
              <th>Status</th>
              {showDelivery && <th>Delivery</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <MemberRow key={m.asset.id} m={m} g={g} canEdit={canEdit} canOrder={canOrder} numbered={numbered} showDelivery={showDelivery}
                selected={selected.has(m.asset.id)} onToggle={() => toggle(m.asset.id)}
                dragging={drag === m.asset.id} over={over === m.asset.id && drag !== m.asset.id}
                onDragStart={() => setDrag(m.asset.id)} onDragOver={() => setOver(m.asset.id)} onDragEnd={() => { setDrag(null); setOver(null); }}
                onDrop={() => { if (drag) void saveOrder(reorder(ids, drag, m.asset.id)); setDrag(null); setOver(null); }}
                onMove={(dir) => {
                  const i = ids.indexOf(m.asset.id);
                  const j = i + dir;
                  if (j < 0 || j >= ids.length) return;
                  const next = [...ids];
                  [next[i], next[j]] = [next[j]!, next[i]!];
                  void saveOrder(next);
                }} />
            ))}
          </tbody>
        </table>
      )}
      {q.hasMore && <button className="btn ghost sm" onClick={q.loadMore} disabled={q.loadingMore}>{q.loadingMore ? 'Loading…' : 'Load more'}</button>}
      {adding && <AddAssetsDialog g={g} existing={new Set(ids)} onClose={() => setAdding(false)} />}
    </section>
  );
}

function MemberRow({ m, g, canEdit, canOrder, numbered, showDelivery, selected, onToggle, dragging, over, onDragStart, onDragOver, onDragEnd, onDrop, onMove }: {
  m: GroupMemberDTO; g: AssetGroupDetailDTO; canEdit: boolean; canOrder: boolean; numbered: boolean; showDelivery: boolean; selected: boolean; onToggle: () => void;
  dragging: boolean; over: boolean; onDragStart: () => void; onDragOver: () => void; onDragEnd: () => void; onDrop: () => void; onMove: (dir: -1 | 1) => void;
}) {
  const patch = usePatchGroupMember();
  const [num, setNum] = useState(m.number === null ? '' : String(m.number));
  useEffect(() => { setNum(m.number === null ? '' : String(m.number)); }, [m.number]);
  const a = m.asset;
  async function saveNumber() {
    const n = num.trim() === '' ? null : Number(num);
    if (n === m.number || (n !== null && !Number.isInteger(n))) { setNum(m.number === null ? '' : String(m.number)); return; }
    try { await patch.mutateAsync({ id: g.id, assetId: a.id, number: n }); } catch (err) { toast(errorMessage(err), 'error'); setNum(m.number === null ? '' : String(m.number)); }
  }
  return (
    <tr className={`group-member ${dragging ? 'dragging' : ''} ${over ? 'drop-before' : ''} ${selected ? 'selected' : ''}`} data-testid="group-member" data-asset-id={a.id}
      draggable={canOrder} onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', a.id); onDragStart(); }}
      onDragOver={(e) => { if (!canOrder) return; e.preventDefault(); onDragOver(); }} onDragEnd={onDragEnd} onDrop={(e) => { e.preventDefault(); onDrop(); }}>
      {canEdit && (
        <td className="group-col-grip">
          <button type="button" className="btn ghost icon sm group-grip" disabled={!canOrder} aria-label={`Move ${a.title}: drag, or press up and down`} title="Drag to reorder"
            onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); onMove(-1); } if (e.key === 'ArrowDown') { e.preventDefault(); onMove(1); } }} data-testid="group-member-grip">
            <GripVertical size={14} />
          </button>
        </td>
      )}
      <td className="group-col-check"><input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Select ${a.title}`} /></td>
      {numbered && (
        <td className="group-col-number">
          {canEdit
            ? <input className="input group-number-input" type="number" min={0} value={num} onChange={(e) => setNum(e.target.value)} onBlur={() => void saveNumber()}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} aria-label={`${g.type.memberLabel} number of ${a.title}`} data-testid="group-member-number" />
            : <span>{m.number ?? ''}</span>}
        </td>
      )}
      <td className="group-col-thumb">{a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : <span className="group-thumb-empty" />}</td>
      <td><Link to={`/assets/${a.id}`} data-testid="group-member-title">{a.title}</Link></td>
      <td className="mono faint">{a.assetCode}</td>
      <td><span className={`pill ${a.status === 'placeholder' ? '' : a.status === 'ready' ? 'ready' : ''}`}>{a.status === 'placeholder' ? 'Placeholder' : a.status.charAt(0).toUpperCase() + a.status.slice(1)}</span></td>
      {showDelivery && (
        <td data-testid="group-member-delivery" data-state={m.delivery?.state ?? ''}>
          {m.delivery ? <span className={`pill ${DELIVERY_PILL[m.delivery.state] ?? ''}`}>{ASSIGNMENT_STATE_LABEL[m.delivery.state]}</span> : <span className="faint">–</span>}
        </td>
      )}
    </tr>
  );
}

/** Add assets: a Library search with checkboxes; the chosen ones go in at the end (a move asks first, as Add to group). */
function AddAssetsDialog({ g, existing, onClose }: { g: AssetGroupDetailDTO; existing: Set<string>; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Map<string, AssetSummary>>(new Map());
  const [confirming, setConfirming] = useState(false);
  const results = useLibrary({ q: search, status: [], kind: [], location: [], category: [], fields: {}, sort: 'newest', includeDismissed: false });
  const items = (results.data?.items ?? []).filter((a) => !existing.has(a.id));
  const add = useAddGroupMembers();
  const toggle = (a: AssetSummary) => setChosen((m) => { const n = new Map(m); if (n.has(a.id)) n.delete(a.id); else n.set(a.id, a); return n; });
  if (confirming) return <AddToGroupDialogFor g={g} assets={[...chosen.values()]} onClose={onClose} />;
  async function go() {
    try {
      const r = await add.mutateAsync({ id: g.id, assetIds: [...chosen.keys()] });
      toast(`${plural(r.added.length + r.moved.length, 'asset')} added to ${g.title}`);
      if (r.refused.length) toast(`Not added: ${r.refused.map((x) => x.message).join('; ')}`, 'error');
      onClose();
    } catch (err) {
      if ((err as { code?: string }).code === 'GROUP_TYPE_TAKEN') { setConfirming(true); return; }
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <Modal title={`Add assets to ${g.title}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!chosen.size || add.isPending} onClick={() => void go()} data-testid="group-add-assets-save"><Plus size={14} /> Add {chosen.size || ''}</button>
    </>}>
      <div className="group-picker-search">
        <Search size={14} className="faint" />
        <input className="input" type="search" autoFocus placeholder="Search the Library" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search the Library" data-testid="group-add-assets-search" />
      </div>
      <ul className="group-asset-pick" data-testid="group-add-assets-list">
        {results.isPending && <li className="faint"><Loader2 size={12} className="spin" /> Searching…</li>}
        {items.map((a) => (
          <li key={a.id}>
            <label>
              <input type="checkbox" checked={chosen.has(a.id)} onChange={() => toggle(a)} data-testid="group-add-asset" />
              {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : <span className="group-thumb-empty" />}
              <span>{a.title}</span><span className="mono faint">{a.assetCode}</span>
              {a.groups?.filter((x) => x.typeId === g.typeId).map((x) => <span key={x.groupId} className="pill" title="Adding it moves it here">in {x.title}</span>)}
            </label>
          </li>
        ))}
        {!results.isPending && !items.length && <li className="faint">No asset matches.</li>}
      </ul>
      {results.hasNextPage && <button className="btn ghost sm" onClick={() => void results.fetchNextPage()}>Load more</button>}
    </Modal>
  );
}

/** After a 409 from Add assets: the same move confirmation as Add to group, already pointed at this group. */
function AddToGroupDialogFor({ g, assets, onClose }: { g: AssetGroupDetailDTO; assets: AssetSummary[]; onClose: () => void }) {
  const add = useAddGroupMembers();
  const taken = assets.filter((a) => a.groups?.some((x) => x.typeId === g.typeId && x.groupId !== g.id));
  async function go() {
    try {
      const r = await add.mutateAsync({ id: g.id, assetIds: assets.map((a) => a.id), move: true });
      toast(`${plural(r.added.length + r.moved.length, 'asset')} added to ${g.title}${r.moved.length ? ` (${r.moved.length} moved)` : ''}`);
      onClose();
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  if (!taken.length) return <AddToGroupDialog assets={assets} typeId={g.typeId} onClose={onClose} />;
  return (
    <Modal title={`Move to ${g.title}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={add.isPending} onClick={() => void go()} data-testid="group-move-confirm">Move {taken.length}</button>
    </>}>
      <p style={{ marginTop: 0 }}>An asset is in one {g.type.label.toLowerCase()} at a time. These will move:</p>
      <ul className="group-move-list">
        {taken.map((a) => <li key={a.id}><span className="mono">{a.assetCode}</span> {a.title} <span className="faint">· now in {a.groups!.find((x) => x.typeId === g.typeId)!.title}</span></li>)}
      </ul>
    </Modal>
  );
}
